import { ArrayHelper, ObjectHelper, ServiceHelper, StringHelper } from '../../../assets/utils/helpers';
import { CustomProcessResponse, GetApplicationSetting, IApplicationSettings, ISnackbarConfiguration, SnackbarConfiguration } from '../../configuration';
import { IActionResponse, IActionResult } from './actionResult';
import { ApiClient, IApiClient } from './apiClient';
import { Constants } from './constants';

type RouteParams = Record<string, string | number | null | undefined>;

export class ApiParams {
    key: string = Constants.emptyString;
    value: any;
    isRequired?: boolean | undefined;
    private overideErrorMessage?: string | undefined;

    constructor(key: string, value: any, isRequired?: boolean | undefined, errorMessage?: string | undefined) {
        this.key = key;
        this.value = value;
        this.isRequired = isRequired;
        this.overideErrorMessage = errorMessage;
    }

    isValid = (): boolean => {
        if (this.isRequired === undefined || this.isRequired === null || this.isRequired === false) {
            return true;
        }

        return this.value !== undefined && this.value !== null;
    };

    ErrorMessage = (): string => {
        if (this.isValid()) {
            return Constants.emptyString;
        }

        const message: string = StringHelper.whenNullOrEmpty(this.overideErrorMessage, `Missing ${this.key}.`);
        return message;
    };
}

export abstract class BaseClient {
    //private snackbarConfiguration: ISnackbarConfiguration;
    private apiClient: IApiClient;
    private baseUrl: string = Constants.emptyString;
    abstract routes: string[];


    constructor(snackbarConfiguration?: ISnackbarConfiguration | undefined) {
        const settings: IApplicationSettings = GetApplicationSetting;
        this.setBaseUrl(settings.serviceSettings.hostUrl);

        //this.snackbarConfiguration = snackbarConfiguration !== undefined && snackbarConfiguration !== null ? snackbarConfiguration : new SnackbarConfiguration();
        this.apiClient = new ApiClient(snackbarConfiguration);
    }

    private setBaseUrl(baseUrl: string) {
        if (StringHelper.isNotNullOrEmpty(baseUrl)) {
            this.baseUrl = baseUrl;
        }
    }

    //setSnackbarConfiguration(snackbarConfiguration: ISnackbarConfiguration) {
    //    if (ObjectHelper.isNotNullOrUndefined(snackbarConfiguration)) {
    //        this.snackbarConfiguration = snackbarConfiguration;
    //        this.apiClient = new ApiClient(snackbarConfiguration);
    //    }
    //}

    // #region Get
  
    async getAsync<T>(actions: string[], params?: ApiParams[], routeParams?: RouteParams, signal?: AbortSignal): Promise<T> {
        const response: IActionResult<T> = await this.getResponseAsync<T, IActionResult<T>>(actions, params, routeParams, signal);
        const result: IActionResult<T> = ServiceHelper.handleActionResult<T>(response);
        return result.result;
    }

    async getResponseAsync<T, U = IActionResult<T>>(actions: string[], params?: ApiParams[], routeParams?: RouteParams, signal?: AbortSignal): Promise<U> {
        const errorMessages = this.ValidateParams(params);
        if (ArrayHelper.isNotEmpty(errorMessages)) {
            return ServiceHelper.CreateErrorResult<T, U>(undefined, errorMessages);
        }

        const routeUrl: string = this.buildRouteUrl(actions, routeParams);
        return await this.apiClient.getAsync<T, U>(routeUrl, this.FormatParams(params), undefined, undefined, signal);
    }

    // #endregion Get

    // #region Post

    async postAsync<T, U = T, V = IActionResult<U>>(actions: string[], body?: T, routeParams?: RouteParams, signal?: AbortSignal): Promise<V> {
        const response: V = await this.postResponseAsync(actions, body, routeParams, signal);
        const result: V = ServiceHelper.handleActionResult<T, U, V>(response);
        return result;
    }

    async postResponseAsync<T, U = T, V = IActionResult<U>>(actions: string[], body?: T, routeParams?: RouteParams, signal?: AbortSignal): Promise<V> {
        const routeUrl: string = this.buildRouteUrl(actions, routeParams);
        return this.apiClient.postAsync<T, U, V>(routeUrl, body, undefined, undefined, undefined, signal);
    }

    // #endregion Post

    // #region Put

    async putAsync<T, U = T, V = IActionResult<U>>(actions: string[], body?: T, routeParams?: RouteParams, signal?: AbortSignal): Promise<V> {
        const response: V = await this.putResponseAsync(actions, body, routeParams, signal);
        const result: V = ServiceHelper.handleActionResult<T, U, V>(response);
        return result;
    }

    async putResponseAsync<T, U = T, V = IActionResult<U>>(actions: string[], body?: T, routeParams?: RouteParams, signal?: AbortSignal): Promise<V> {
        const routeUrl: string = this.buildRouteUrl(actions, routeParams);
        return this.apiClient.putAsync<T, U, V>(routeUrl, body, undefined, undefined, undefined, signal);
    }

    // #endregion Put

    // #region Delete

    async deleteAsync(actions: string[], params?: ApiParams[], routeParams?: RouteParams, signal?: AbortSignal): Promise<IActionResponse> {
        const response: IActionResponse = await this.deleteResponseAsync(actions, params, routeParams, signal);
        return response;
    }

    async deleteResponseAsync(actions: string[], params?: ApiParams[], routeParams?: RouteParams, signal?: AbortSignal) {
        const errorMessages = this.ValidateParams(params);
        if (ArrayHelper.isNotEmpty(errorMessages)) {
            return ServiceHelper.CreateErrorResponse(errorMessages);
        }

        const routeUrl: string = this.buildRouteUrl(actions, routeParams);
        return this.apiClient.deleteAsync(routeUrl, this.FormatParams(params), undefined, signal);
    }

    // #endregion Delete


    private buildUrl = (items: string[] | undefined): string => {
        if (ArrayHelper.isEmpty(items)) {
            return Constants.emptyString;
        }

        const routeUrl: string = items?.join('/') ?? Constants.emptyString;
        return routeUrl;
    };

    private buildRouteUrl = (actions?: string[] | undefined, routeParams?: RouteParams) => {
        let url: string = Constants.emptyString;

        const routeUrl: string = this.buildUrl(this.routes);
        if (StringHelper.isNotNullOrEmpty(routeUrl)) {
            url = StringHelper.enforceEndsWith(url, '/') + routeUrl;
        }

        const actionUrl: string = this.buildUrl(actions);
        if (StringHelper.isNotNullOrEmpty(actionUrl)) {
            url = StringHelper.enforceEndsWith(url, '/') + actionUrl;
        }

        url = StringHelper.replaceRouteParams(url, routeParams);
        url = url.replaceAll('//', '/');

        return `${StringHelper.enforceEndsWith(this.baseUrl, '/')}${url}`;
    };

    private ValidateParams(params: ApiParams[] | undefined): string[] {
        if (ArrayHelper.isEmpty(params)) {
            return [];
        }

        const result: string[] = [];
        params?.map((item) => {
            const message: string = item.ErrorMessage();
            if (StringHelper.isNotNullOrEmpty(message)) {
                result.push(message);
            }
        });

        return result;
    }

    private FormatParams(params: ApiParams[] | undefined): Record<string, any> {
        if (ArrayHelper.isEmpty(params)) {
            return {};
        }

        let result: Record<string, any> = {};
        params?.map((item) => {
            if (item) {
                result[item.key] = item.value;
            }
        });

        return result;
    }

    //Process Response
    private ProcessResponse(_response: IActionResponse): void {
        //List of all processes
        CustomProcessResponse(_response);
        this.HandleSnackbar(_response);
    }

    private HandleSnackbar(_response: IActionResponse): void {
        //TODO: Handle  Snackbar code
    }
}
